/**
 * ==========================================================================
 *  EingebettetesWidget.jsx — Branded Embed-Variante des BookingWidgets
 * ==========================================================================
 *  Wird von externen Webseiten per <iframe> eingebettet.
 *  URL-Schema:
 *    /widget/pd  → pflege-dschungel.de  (Markenprofil 'pd')
 *    /widget/spa → spa-pflegeberatung.de (Markenprofil 'spa', kommt spaeter)
 *
 *  Layout:
 *    Desktop (md+): zweispaltig — links Vertrauensblock (Foto, USPs),
 *                    rechts der bestehende mehrstufige Wizard.
 *    Mobile:        gestapelt — Vertrauensblock oben, Wizard drunter.
 *
 *  Eingebettet via:
 *    <iframe src="https://beratungs-cockpit.de/widget/pd"
 *            style="width:100%; height:1200px; border:0;"></iframe>
 *
 *  Iframe-Hoehe wird per postMessage an den Parent geschickt — der Parent
 *  kann sie via JS dynamisch anpassen. Wenn er das nicht tut, reicht eine
 *  feste min-height (s.o.) fuer alle Faelle.
 * ==========================================================================
 */

import React, { useEffect, useRef } from 'react';
import { Video, Home as HomeIcon, ShieldCheck, CheckCircle, Clock, MapPin } from 'lucide-react';
import BookingWidget from './BookingWidget';

// --------------------------------------------------------------------------
// Marken-Profile — Farben, Logo, Texte, externe Links pro Webseite
// --------------------------------------------------------------------------
const MARKEN = {
  pd: {
    name: 'Pflege-Dschungel',
    domain: 'pflege-dschungel.de',
    primary: '#85366C',     // brombeer
    akzent: '#DC91D0',      // orchidee
    logoText: 'Pflege-Dschungel',
    portraitUrl: '/portrait-dohmeyer.png',
    portraitName: 'Hendrik Dohmeyer',
    portraitRolle: 'Pflegeberater nach § 7a SGB XI',
    headline: 'Kostenfreie Pflegeberatung',
    subline: 'Persönlich, unkompliziert — per Video oder bei Ihnen vor Ort.',
    usps: [
      { icon: ShieldCheck, text: 'Anerkannt nach § 37.3 SGB XI — für Versicherte mit Pflegegrad kostenfrei' },
      { icon: Video, text: 'Video- oder Hausbesuch frei wählbar' },
      { icon: MapPin, text: 'Verfügbare Regionen werden direkt angezeigt' },
      { icon: CheckCircle, text: 'Über 5 Jahre Erfahrung in der Pflegeberatung' },
    ],
    datenschutzUrl: 'https://pflege-dschungel.de/datenschutz/',
    impressumUrl: 'https://pflege-dschungel.de/impressum/',
  },
  spa: {
    name: 'SPA Pflegeberatung',
    domain: 'spa-pflegeberatung.de',
    primary: '#85366C',     // platzhalter — wird ersetzt, sobald Hendrik die SPA-Farben gibt
    akzent: '#DC91D0',
    logoText: 'SPA Pflegeberatung',
    portraitUrl: '/portrait-dohmeyer.png',
    portraitName: 'Hendrik Dohmeyer',
    portraitRolle: 'Pflegeberater nach § 7a SGB XI',
    headline: 'Kostenfreie Pflegeberatung',
    subline: 'Persönlich und vertraulich — per Video oder vor Ort.',
    usps: [
      { icon: ShieldCheck, text: 'Anerkannt nach § 37.3 SGB XI' },
      { icon: Video, text: 'Wahlweise Video- oder Hausbesuch' },
      { icon: CheckCircle, text: 'Erfahrene Pflegeberatung in Bremen und Umgebung' },
      { icon: Clock, text: 'Termine kurzfristig verfügbar' },
    ],
    datenschutzUrl: 'https://spa-pflegeberatung.de/datenschutz/',
    impressumUrl: 'https://spa-pflegeberatung.de/impressum/',
  },
  // Variante fuer das Floating-CTA-Popup auf pflege-dschungel.de —
  // gleicher Look wie 'pd', nur die Headline ist auf den Halbjahres-
  // Beratungstermin zugespitzt.
  'pd-popup': {
    name: 'Pflege-Dschungel (Popup)',
    domain: 'pflege-dschungel.de',
    primary: '#85366C',
    akzent: '#DC91D0',
    logoText: 'Pflege-Dschungel',
    portraitUrl: '/portrait-dohmeyer.png',
    portraitName: 'Hendrik Dohmeyer',
    portraitRolle: 'Pflegeberater nach § 7a SGB XI',
    headline: 'Buchen Sie jetzt noch für das erste Halbjahr Ihren Beratungstermin',
    subline: 'Kostenfreie Pflegeberatung — per Video oder bei Ihnen vor Ort.',
    usps: [
      { icon: ShieldCheck, text: 'Anerkannt nach § 37.3 SGB XI — für Versicherte mit Pflegegrad kostenfrei' },
      { icon: Video, text: 'Video- oder Hausbesuch frei wählbar' },
      { icon: MapPin, text: 'Verfügbare Regionen werden direkt angezeigt' },
      { icon: CheckCircle, text: 'Über 5 Jahre Erfahrung in der Pflegeberatung' },
    ],
    datenschutzUrl: 'https://pflege-dschungel.de/datenschutz/',
    impressumUrl: 'https://pflege-dschungel.de/impressum/',
  },
};

export default function EingebettetesWidget({ marke = 'pd' }) {
  const profil = MARKEN[marke] || MARKEN.pd;
  const containerRef = useRef(null);

  // Initialen aus dem Namen — Fallback hinter dem Foto, falls das Bild
  // (noch) nicht hochgeladen ist. Das echte <img> liegt im Layer drueber
  // und verdeckt die Initialen, sobald es laedt; bei 404 schaltet onError
  // das img unsichtbar und die Initialen scheinen durch.
  const initialen = profil.portraitName
    .split(' ')
    .map(w => w[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // Hoehe an den Parent (iframe-Container) schicken — falls der Parent
  // ein passendes onMessage-Handler hat, kann er die iframe-Hoehe
  // dynamisch anpassen. Wenn nicht: kein Schaden, einfach ignoriert.
  useEffect(() => {
    if (!containerRef.current) return;
    const sendeHoehe = () => {
      const h = containerRef.current?.scrollHeight;
      if (h) {
        try {
          window.parent?.postMessage({ type: 'cockpit-widget-height', height: h }, '*');
        } catch { /* ignorieren — Cross-Origin ohne passenden Listener */ }
      }
    };
    sendeHoehe();
    // ResizeObserver fuer Layout-Aenderungen (Wizard-Schritte, Tastatur-Aufpopup auf Handy)
    const ro = new ResizeObserver(sendeHoehe);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* === Linke Spalte: Vertrauensblock — clean weiss, brombeer nur als Akzent === */}
        <aside className="md:col-span-1 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          {/* Foto kreisrund — mit Initialen-Fallback, falls das Bild fehlt.
              Kleinerer Akzent-Rand statt grossflaechiger Fuellung. */}
          <div className="flex flex-col items-center text-center mb-5">
            <div
              className="w-24 h-24 rounded-full overflow-hidden shadow-md mb-3 relative flex items-center justify-center ring-4 ring-white"
              style={{ backgroundColor: profil.akzent }}
            >
              <span className="text-2xl font-bold text-white/95 select-none">{initialen}</span>
              <img
                src={profil.portraitUrl}
                alt={profil.portraitName}
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <div className="font-bold text-base text-gray-800 leading-tight">{profil.portraitName}</div>
            <div className="text-xs text-gray-500 mt-0.5">{profil.portraitRolle}</div>
          </div>

          {/* Akzent-Linie statt grossflaechiger Farbe */}
          <div className="h-0.5 w-12 mx-auto rounded-full mb-5" style={{ backgroundColor: profil.primary }}></div>

          <h2 className="text-lg font-bold text-gray-800 mb-2 leading-snug text-center">{profil.headline}</h2>
          <p className="text-sm text-gray-600 mb-5 leading-relaxed text-center">{profil.subline}</p>

          <ul className="space-y-2.5">
            {profil.usps.map((usp, i) => {
              const Icon = usp.icon;
              return (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: profil.primary }} />
                  <span className="text-gray-700 leading-snug">{usp.text}</span>
                </li>
              );
            })}
          </ul>

          <div className="mt-6 pt-4 border-t border-gray-100 text-[11px] text-gray-400 leading-relaxed">
            Mit Klick auf „Termin buchen" akzeptieren Sie die <a href={profil.datenschutzUrl} target="_blank" rel="noreferrer" className="underline hover:text-gray-600">Datenschutzhinweise</a>.
            Anbieter siehe <a href={profil.impressumUrl} target="_blank" rel="noreferrer" className="underline hover:text-gray-600">Impressum</a>.
          </div>
        </aside>

        {/* === Rechte Spalte: Wizard === */}
        <main className="md:col-span-2">
          <BookingWidget embedded />
        </main>
      </div>
    </div>
  );
}
